import CallService from "@/lib/calls"
import { useContext, useEffect, useRef, useState } from "react"
import { VibratoClientContext } from "../contexts"
import {
  LocalParticipant,
  LocalTrackPublication,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  Track,
  VideoPresets,
  RoomEvent
} from "livekit-client"
import { settings } from "@/settings"
import {
  SipDTMF,
  queueDtmfTone,
  clearDtmfQueue,
  setSharedAudioContext,
  cleanupAudioContext as cleanupDtmfAudioSystem
} from "@/lib/dtmf"

// Function to handle DTMF playback
const playbackDtmf = async (dtmf: SipDTMF, sharedAudioContext?: AudioContext): Promise<void> => {
  if (!dtmf || !dtmf.digit) {
    console.warn("Invalid DTMF received")
    return
  }

  // Add the DTMF tone to the queue, passing in the shared context
  queueDtmfTone(dtmf, sharedAudioContext)
}

export default function LiveCallPlayback({
  callId,
  playCall,
  isPlaying,
  setIsPlaying
}: {
  callId: number
  playCall: any
  isPlaying: boolean
  setIsPlaying: any
}) {
  const vibratoClient = useContext(VibratoClientContext)
  const [currentState, setCurrentState] = useState("idle")

  const call = useRef<any>(null)
  const callSvc = useRef<CallService | null>(null)

  const livekitRoom = useRef<Room | null>(null)
  const livekitToken = useRef<string | null>(null)
  const audioContext = useRef<AudioContext | null>(null)

  // Fetch call object once we're ready to listen.
  useEffect(() => {
    if (currentState != "idle") {
      return
    }
    if (!vibratoClient) {
      console.log("Vibrato client not initialized, not fetching call")
      return
    }
    if (!callId) {
      console.log("Call ID not provided, not fetching call")
      return
    }

    vibratoClient.getCall(callId).then(async (result: any) => {
      const svc = new CallService(result)

      livekitRoom.current = result.livekit_room_name

      call.current = result
      callSvc.current = svc

      audioContext.current = new AudioContext()
      console.log("AudioContext created for LiveCallPlayback:", audioContext.current.state)
      setSharedAudioContext(audioContext.current)

      await initClients(result.livekit_room_name)

      setCurrentState("ready")

      console.log("Fetched call: ", result)
    })
  }, [vibratoClient, callId])

  function handleTrackSubscribed(
    track: RemoteTrack,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _publication: RemoteTrackPublication,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _participant: RemoteParticipant
  ) {
    if (track.kind === Track.Kind.Video) {
      // Always attach video tracks
      const element = track.attach()
      document.body.appendChild(element)
    } else if (track.kind === Track.Kind.Audio) {
      // Always attach audio tracks since:
      // - On desktop: we need DOM audio alongside webAudioMix for playback
      // - On mobile: we're not using webAudioMix, so DOM audio is the only playback method
      const element = track.attach()
      document.body.appendChild(element)
    }
  }

  function handleTrackUnsubscribed(
    track: RemoteTrack,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _publication: RemoteTrackPublication,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _participant: RemoteParticipant
  ) {
    // Detach all tracks that were attached to DOM
    if (track.kind === Track.Kind.Video || track.kind === Track.Kind.Audio) {
      track.detach()
    }
  }

  function handleLocalTrackUnpublished(
    publication: LocalTrackPublication,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _participant: LocalParticipant
  ) {
    // when local tracks are ended, update UI to remove them from rendering
    if (publication.track) {
      publication.track.detach()
    }
  }

  function handleSipDTMF(
    dtmf: SipDTMF,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _participant?: RemoteParticipant
  ) {
    console.log("SIP DTMF received: ", dtmf)
    // Play the DTMF tone through the user's audio device using the shared context
    playbackDtmf(dtmf, audioContext.current || undefined).catch((error) => {
      console.error("Failed to play DTMF tone:", error)
    })
  }

  function handleDisconnect() {
    console.log("disconnected from room")
    setCurrentState("ready")
    setIsPlaying(false)
  }

  const initClients = async (roomName: string) => {
    let jwt = null
    try {
      jwt = await vibratoClient.getLivekitToken(roomName)
    } catch (e) {
      console.log("Error getting Livekit token: ", e)
    }

    livekitToken.current = jwt

    // Create room configuration - conditionally use webAudioMix
    const roomConfig: any = {
      // automatically manage subscribed video quality
      adaptiveStream: true,

      // optimize publishing bandwidth and CPU for published tracks
      dynacast: true,

      // default capture settings
      videoCaptureDefaults: {
        resolution: VideoPresets.h720.resolution
      }
    }

    // Only use webAudioMix on desktop to avoid audio playback issues on mobile
    if (!settings.IS_NATIVE_APP) {
      roomConfig.webAudioMix = {
        audioContext: audioContext.current! // Assert non-null as it's initialized before this
      }
    }

    // creates a new room with options
    const room = new Room(roomConfig)

    room
      .on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
      .on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
      .on(RoomEvent.Disconnected, handleDisconnect)
      .on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished)
      .on(RoomEvent.SipDTMFReceived, handleSipDTMF)
    // pre-warm connection, this can be called as early as your page is loaded
    room.prepareConnection(settings.LIVEKIT_URL, jwt)

    livekitRoom.current = room

    console.log("Livekit room initialized", livekitRoom.current)
  }

  // Clear DTMF queue on component unmount
  useEffect(() => {
    return () => {
      setCurrentState("idle")
      disconnectLivekit().catch((e) => {
        console.log("Error disconnecting Livekit: ", e)
      })

      // Clean up audio context
      if (audioContext.current) {
        // Close the context we created
        audioContext.current
          .close()
          .then(() => {
            console.log("Component-managed AudioContext closed.")
          })
          .catch((e) => console.error("Error closing component-managed AudioContext", e))
        // Call the DTMF system's cleanup if it needs to do anything specific beyond context closure
        cleanupDtmfAudioSystem()
        audioContext.current = null
      }

      // Clear any queued DTMF tones when component unmounts
      clearDtmfQueue()
    }
  }, [])

  const disconnectLivekit = async () => {
    if (livekitRoom.current == null) {
      console.log("Livekit room not initialized, can't disconnect")
      return
    }

    await livekitRoom.current.disconnect()

    console.log("Livekit room disconnected")
  }

  const startCallPlayback = async () => {
    if (call.current == null) {
      console.log("Call object does not exist, can't start playback")
      return
    }
    if (livekitRoom.current == null) {
      console.log("Livekit room not initialized, can't start playback")
      return
    }
    if (livekitToken.current == null) {
      console.log("Livekit token is null, can't start playback")
      return
    }
    if (isPlaying) {
      console.log("Playback already in progress, can't start playback")
      return
    }

    console.log("Starting playback of call: ", call.current, settings.LIVEKIT_URL, livekitRoom.current)

    await livekitRoom.current.connect(settings.LIVEKIT_URL, livekitToken.current)
    console.log("connected to room", livekitRoom.current.name)

    // Attempt to resume our AudioContext via LiveKit's startAudio or directly
    if (audioContext.current && audioContext.current.state === "suspended") {
      console.log("Attempting to resume shared AudioContext before starting LiveKit audio handling.")
      await audioContext.current.resume().catch((e) => console.error("Error resuming shared AudioContext directly", e))
    }

    await livekitRoom.current.startAudio()
    console.log("LiveKit startAudio called. Current shared AudioContext state:", audioContext.current?.state)

    // Ensure the DTMF system is using our component-managed context.
    // This might be redundant if setSharedAudioContext was already called when audioContext was created,
    // but it's safe to call again to ensure consistency.
    if (audioContext.current) {
      setSharedAudioContext(audioContext.current)
    }

    setIsPlaying(true)

    console.log("Playback started successfully")

    return null
  }

  const stopCallPlayback = async () => {
    if (livekitRoom.current == null) {
      console.log("Livekit room not initialized, can't stop playback")
      return
    }
    if (!isPlaying) {
      console.log("Playback not in progress, can't stop playback")
      return
    }

    await disconnectLivekit()

    setIsPlaying(false)

    console.log("Call playback stopped successfully")

    return null
  }

  useEffect(() => {
    if (currentState === "ready" && call.current) {
      if (playCall && !isPlaying) {
        startCallPlayback().catch((e) => {
          console.log("Error starting playback: ", e)
        })
      }
    }
    if (!playCall && isPlaying) {
      stopCallPlayback().catch((e) => {
        console.log("Error stopping playback: ", e)
      })
    }
  }, [currentState, playCall, isPlaying])

  return null
}
